// src/components/Onboarding/ResultTimeDisplay.js

import React, { useEffect, useState, useRef } from 'react';
import WebGlGradientCanvas from './WebGlGradientCanvas';
import singleLaurel from '../../images/single_laurel.svg';

function SlotMachine({ finalValue, allValues, durationMs = 2000, delayMs = 0, onAnimationComplete }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const finalIndex = allValues.indexOf(finalValue);

  useEffect(() => {
    if (finalIndex === -1) return;

    const timeout = setTimeout(() => {
      setCurrentIndex(finalIndex);
      
      // Notify parent when animation completes
      if (onAnimationComplete) {
        setTimeout(() => {
          onAnimationComplete();
        }, durationMs);
      }
    }, delayMs);

    return () => clearTimeout(timeout);
  }, [finalIndex, delayMs, durationMs, onAnimationComplete]);

  if (finalIndex === -1) {
    return <span>{finalValue}</span>;
  }

  const itemHeight = 48; // Fixed height in pixels

  return (
    <div 
      className="inline-block overflow-hidden align-top relative"
      style={{ 
        height: `${itemHeight}px`,
        lineHeight: `${itemHeight}px`,
        WebkitMaskImage: 'linear-gradient(to bottom, transparent 0%, black 25%, black 75%, transparent 100%)',
        maskImage: 'linear-gradient(to bottom, transparent 0%, black 25%, black 75%, transparent 100%)',
      }}
    >
      <div
        style={{
          transform: `translateY(-${currentIndex * itemHeight}px)`,
          transition: `transform ${durationMs}ms cubic-bezier(0.4, 0.0, 0.2, 1.1)`,
        }}
      >
        {allValues.map((value, idx) => (
          <div
            key={idx}
            style={{
              height: `${itemHeight}px`,
              lineHeight: `${itemHeight}px`,
            }}
          >
            {value}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function ResultTimeDisplay({ 
  monthsFromNow = 9,
  // Rainbow underglow customization
  enableRainbowGlow = true,
  glowColors = ['#ff00ff', '#00ffff', '#ffff00', '#ff0080', '#00ff80', '#ff8000'],
  glowIntensity = 1,
  glowSpread = 20,
  glowBlur = 50,
  glowDelay = 500, // Delay after animation completes before glow starts
  // Inner neon glow (tight, close to letters)
  innerGlowIntensity = 0.8, // Intensity multiplier for inner glow
  innerGlowBlur = 15, // Blur amount for inner glow (px)
  innerGlowSpread = 1, // Spread for inner glow (px)
  // Variation parameters (for post-boom irregular shifting)
  intensityVariation = 0.05, // Percentage (0-1) of intensity to vary
  spreadVariation = 0.1, // Percentage (0-1) of spread to vary
  variationSpeed = 3000, // Time in ms for one variation cycle
  variationIrregularity = 0.9, // How irregular the variation is (0-1, higher = more random)
  colorShiftSpeed = 500, // Time in ms for color rotation cycle
  onComplete = null, // Callback when all animations (slot + glow) are complete
}) {
  const now = new Date();
  const currentMonth = now.getMonth();
  const currentYear = now.getFullYear();

  const targetDate = new Date(now);
  targetDate.setMonth(currentMonth + monthsFromNow);
  
  const targetMonthIndex = targetDate.getMonth();
  const targetYear = targetDate.getFullYear();

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  // Build a sequence of "Month Year" strings from current to target
  const dateSequence = [];
  for (let i = 0; i <= monthsFromNow; i++) {
    const date = new Date(now);
    date.setMonth(currentMonth + i);
    const monthName = monthNames[date.getMonth()];
    const year = date.getFullYear();
    dateSequence.push(`${monthName} ${year}`);
  }

  const finalDateString = `${monthNames[targetMonthIndex]} ${targetYear}`;

  // Track animation completion
  const [animComplete, setAnimComplete] = useState(false);
  const [showGlow, setShowGlow] = useState(false);
  const [glowFadeProgress, setGlowFadeProgress] = useState(0);
  const [laurelAnimProgress, setLaurelAnimProgress] = useState(0);
  const [variationOffset, setVariationOffset] = useState(0); // For irregular spread/intensity variation
  const [gradientKey, setGradientKey] = useState(0); // Force gradient sync by remounting

  // Use ref for onComplete to avoid re-running effect when callback changes
  const onCompleteRef = useRef(onComplete);
  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  // Calculate when animation is done
  const totalAnimTime = 800 + 4000; // delay + duration

  useEffect(() => {
    if (!enableRainbowGlow) return;

    const timer = setTimeout(() => {
      setShowGlow(true);
      setGradientKey(prev => prev + 1); // Force both gradients to remount at same time
      
      // Explosive boom animation: fast spike (300ms) + gradual settle (1500ms)
      const boomDuration = 1200; // Fast initial explosion
      const settleDuration = 500; // Gradual calm down
      const totalDuration = boomDuration + settleDuration;
      const laurelAnimDuration = 600; // Laurel entrance animation duration
      const startTime = performance.now();
      
      const animate = (currentTime) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / totalDuration, 1);
        
        // Laurel animation (faster, completes in 600ms)
        const laurelProgress = Math.min(elapsed / laurelAnimDuration, 1);
        const laurelEased = 1 - Math.pow(1 - laurelProgress, 3); // easeOutCubic for smooth entrance
        setLaurelAnimProgress(laurelEased);
        
        let eased;
        if (elapsed < boomDuration) {
          // Phase 1: Fast explosion (0 to 1 in 300ms)
          const boomProgress = elapsed / boomDuration;
          eased = Math.pow(boomProgress, 0.5); // Square root for fast ramp up
        } else {
          // Phase 2: Gradual settle (overshoot then ease to final)
          const settleProgress = (elapsed - boomDuration) / settleDuration;
          // Custom easing: overshoot slightly then settle
          const overshoot = 1.15; // Peak at 115%
          if (settleProgress < 0.3) {
            // Quick rise to overshoot
            const t = settleProgress / 0.3;
            eased = 1 + (overshoot - 1) * Math.pow(t, 2);
          } else {
            // Gradual settle from overshoot to 1.0
            const t = (settleProgress - 0.3) / 0.7;
            const easeOut = 1 - Math.pow(1 - t, 3);
            eased = overshoot - (overshoot - 1) * easeOut;
          }
        }
        
        setGlowFadeProgress(Math.min(eased, 1));
        
        if (progress < 1) {
          requestAnimationFrame(animate);
        } else {
          setGlowFadeProgress(1);
          setLaurelAnimProgress(1); // Finalize laurel animation
          // Notify parent that all animations are complete
          if (onCompleteRef.current && typeof onCompleteRef.current === 'function') {
            onCompleteRef.current();
          }
          
          // Start irregular variation loop after boom completes
          const variationStartTime = performance.now();
          const varyLoop = (currentTime) => {
            if (!enableRainbowGlow) return;
            
            const elapsed = currentTime - variationStartTime;
            const cycle = (elapsed % variationSpeed) / variationSpeed;
            
            // Create irregular variation using multiple sine waves at different frequencies
            const wave1 = Math.sin(cycle * Math.PI * 2);
            const wave2 = Math.sin(cycle * Math.PI * 4 + 1.5);
            const wave3 = Math.sin(cycle * Math.PI * 6 + 3);
            
            // Combine waves with irregularity factor
            const irregular = wave1 * (1 - variationIrregularity) + 
                            wave2 * variationIrregularity * 0.6 + 
                            wave3 * variationIrregularity * 0.4;
            
            setVariationOffset(irregular);
            requestAnimationFrame(varyLoop);
          };
          requestAnimationFrame(varyLoop);
        }
      };
      
      requestAnimationFrame(animate);
    }, totalAnimTime + glowDelay);

    return () => clearTimeout(timer);
  }, [enableRainbowGlow, totalAnimTime, glowDelay, variationSpeed, variationIrregularity]);

  // Laurel leaf positioning parameters (easily adjustable)
  const laurelConfig = {
    rotation: -17,        // Rotation angle (degrees) - will be reversed for left side
    scale: 1.4,         // Scale factor
    opacity: 0.9,       // Opacity (0-1)
    // Animation parameters
    initialScale: 1,  // Starting scale (60% of final)
    initialOffset: 30,  // Starting position offset inward (px)
  };

  // Calculate animated laurel properties
  const laurelScaleAnim = laurelConfig.initialScale + (laurelConfig.scale - laurelConfig.initialScale) * laurelAnimProgress;
  const laurelOffsetAnim = laurelConfig.initialOffset * (1 - laurelAnimProgress);
  const laurelOpacityAnim = laurelConfig.opacity * laurelAnimProgress;

  return (
    <div className="w-full flex items-center justify-center gap-0">
      {/* Left laurel leaf (horizontally flipped) */}
      <div 
        className="flex-shrink-0 pointer-events-none -mr-4"
        style={{
          transform: `translateX(${laurelOffsetAnim}px) rotate(${-laurelConfig.rotation}deg) scaleX(-1) scale(${laurelScaleAnim})`,
          opacity: showGlow ? laurelOpacityAnim : 0,
          transition: 'none', // Controlled by RAF for smooth animation
        }}
      >
        <img src={singleLaurel} alt="" className="w-16 h-16" />
      </div>

      {/* Center content */}
      <div className="flex-shrink-0 text-center">
      {/* Top static text */}
      <div className="text-white/60 text-sm font-light -mb-1">
        You can have your hair back by
      </div>

      {/* Middle animated section with rainbow underglow */}
      <div className="relative inline-block">
        {/* WebGL gradient canvas for underglow effect (wide spread) */}
        {showGlow && (() => {
          // Explosive start: 6x intensity during boom, settle to target
          const boomMultiplier = 6;
          const spreadMultiplier = 5.0;
          const initialIntensity = glowIntensity * boomMultiplier;
          const initialSpread = glowSpread * spreadMultiplier;
          const baseIntensity = initialIntensity - (initialIntensity - glowIntensity) * Math.min(glowFadeProgress, 1);
          const baseSpread = initialSpread - (initialSpread - glowSpread) * Math.min(glowFadeProgress, 1);
          
          // Apply irregular variation after boom completes (glowFadeProgress >= 1)
          const isPostBoom = glowFadeProgress >= 1;
          const currentIntensity = isPostBoom 
            ? baseIntensity * (1 + variationOffset * intensityVariation)
            : baseIntensity;
          const currentSpread = isPostBoom
            ? baseSpread * (1 + variationOffset * spreadVariation)
            : baseSpread;
          
          return (
            <div 
              className="absolute inset-0 pointer-events-none"
              style={{
                left: `-${currentSpread}px`,
                right: `-${currentSpread}px`,
                top: `-${currentSpread}px`,
                bottom: `-${currentSpread}px`,
                filter: `blur(${glowBlur}px)`,
                opacity: currentIntensity * Math.min(glowFadeProgress, 1),
                mixBlendMode: 'screen',
                zIndex: -1,
              }}
            >
              <WebGlGradientCanvas
                key={`outer-${gradientKey}`}
                className="w-full h-full"
                colorShiftSpeed={colorShiftSpeed}
                style={{
                  ['--gradient-color-1']: glowColors[0] || '#ff00ff',
                  ['--gradient-color-2']: glowColors[1] || '#00ffff',
                  ['--gradient-color-3']: glowColors[2] || '#ffff00',
                  ['--gradient-color-4']: glowColors[3] || '#ff0080',
                }}
              />
            </div>
          );
        })()}

        {/* Intense neon glow layer (tight, saturated, close to letters) */}
        {showGlow && innerGlowIntensity > 0 && (() => {
          const boomMultiplier = 6;
          const initialIntensity = glowIntensity * boomMultiplier;
          const baseIntensity = initialIntensity - (initialIntensity - glowIntensity) * Math.min(glowFadeProgress, 1);
          
          // Apply variation to neon layer as well
          const isPostBoom = glowFadeProgress >= 1;
          const currentNeonIntensity = isPostBoom 
            ? baseIntensity * (1 + variationOffset * intensityVariation) * innerGlowIntensity
            : baseIntensity * innerGlowIntensity;
          
          return (
            <div 
              className="absolute inset-0 pointer-events-none"
              style={{
                left: `-${innerGlowSpread}px`,
                right: `-${innerGlowSpread}px`,
                top: `-${innerGlowSpread}px`,
                bottom: `-${innerGlowSpread}px`,
                filter: `blur(${innerGlowBlur}px)`,
                opacity: currentNeonIntensity * Math.min(glowFadeProgress, 1),
                mixBlendMode: 'screen',
                zIndex: 0,
              }}
            >
              <WebGlGradientCanvas
                key={`inner-${gradientKey}`}
                className="w-full h-full"
                colorShiftSpeed={colorShiftSpeed}
                style={{
                  ['--gradient-color-1']: glowColors[0] || '#ff00ff',
                  ['--gradient-color-2']: glowColors[1] || '#00ffff',
                  ['--gradient-color-3']: glowColors[2] || '#ffff00',
                  ['--gradient-color-4']: glowColors[3] || '#ff0080',
                }}
              />
            </div>
          );
        })()}
        
        {/* Text without additional filters - glow comes from WebGL layers only */}
        <div 
          className="text-white text-3xl sm:text-5xl font-bold inline-flex items-center justify-center gap-3 relative z-10"
        >
          <SlotMachine 
            finalValue={finalDateString}
            allValues={dateSequence}
            durationMs={4000}
            delayMs={800}
            onAnimationComplete={() => setAnimComplete(true)}
          />
        </div>
      </div>

      {/* Bottom static text - appears with glow */}
      <div 
        className="text-white/90 text-sm font-light transition-opacity duration-300"
        style={{ opacity: showGlow ? 1 : 0 }}
      >
        ~{monthsFromNow} months from now
      </div>
      </div>

      {/* Right laurel leaf */}
      <div 
        className="flex-shrink-0 pointer-events-none -ml-4"
        style={{
          transform: `translateX(${-laurelOffsetAnim}px) rotate(${laurelConfig.rotation}deg) scale(${laurelScaleAnim})`,
          opacity: showGlow ? laurelOpacityAnim : 0,
          transition: 'none', // Controlled by RAF for smooth animation
        }}
      >
        <img src={singleLaurel} alt="" className="w-16 h-16" />
      </div>
    </div>
  );
}
